import axios from "axios";

import { clientApiCall } from "./apiCallWrapper";
import type {
  EdostCalcPayload,
  EdostCalcResponse,
  EdostCalculatePayload,
  EdostCalculateResponse,
  EdostDataResponse,
  EdostMinDaysResponse,
  EdostOfficesPayload,
  EdostOfficesResponse,
} from "./types/edost";

/**
 * Клиентский (браузерный) API доставки eDost.
 * Ничего не знает о внешних бэкендах — ходит только в свой же Next.js BFF
 * (pages/api/client/edost/*), который уже проксирует запрос к реальным сервисам.
 */
export default (_axios: any, config: any) => ({
  /**
   * Где вызывается: redux/action-creaters/location.js → getEdostMinDays().
   * Диспатчится из всех лейаутов (components/layouts/main, mainNew, landings,
   * baltex) при смене города доставки — то есть срабатывает на КАЖДОЙ
   * странице сайта, не привязано к конкретной странице.
   */
  getMinDays(
    cityName: string,
    region = "",
    zip = "",
  ): Promise<EdostMinDaysResponse | null> {
    return clientApiCall<EdostMinDaysResponse>(
      `${config.API_SERVER_URL}/client/edost/getMinDays/`,
      {
        api: "edost.getMinDays",
        params: { cityName, region, zip },
        skipForRobots: true,
      },
    );
  },
  /**
   * Legacy Bitrix bxApi — напрямую, без BFF (страница товара, старый блок edost).
   * Где вызывается: product/bottom/deliveryNew/edost, productNew/bottom/deliveryNew/edost.
   * удалится после перехода !!!
   */
  getCalc(
    params: EdostCalcPayload,
    isReset = false,
  ): Promise<EdostCalcResponse | null> {
    const qs = Object.entries({
      ...params,
      region_name: encodeURIComponent(String(params.region_name ?? "")),
      city_name_new: encodeURIComponent(params.city_name_new),
    })
      .map(([key, value]) => `${key}=${value}`)
      .join("&");
    let url = `${config.API_URL}/edost/getCalcNewNew?${qs}`;
    if (isReset) {
      url += "&clear_cache=true";
    }
    return clientApiCall<EdostCalcResponse>(url, { api: "edost.getCalc" });
  },
  /**
   * Где вызывается: redux/action-creaters/cart.js → getEdostDataNew().
   * Диспатчится из:
   *  - components/pages/personal/cartNew/useCartLogic.ts — страница /personal/cartNew/;
   *  - components/pages/productNew/bottom/deliveryV2/useProductDeliveryLogic.ts —
   *    страница товара /catalog/[...code]/ (новый блок доставки, ?isNewEdost).
   */
  calculate(
    payload: Partial<EdostCalculatePayload>,
  ): Promise<EdostCalculateResponse | null> {
    return clientApiCall<EdostCalculateResponse>(
      `${config.API_SERVER_URL}/client/edost/calculate/`,
      // Нужен для выбора доставки при оформлении заказа — сообщаем о любом сбое
      {
        api: "edost.calculate",
        critical: true,
        method: "post",
        params: payload,
      },
    );
  },
  /**
   * ПВЗ всех ТК (список companyId захардкожен на BFF). GET — кэш в Redis.
   * Где вызывается: cart.js getEdostDataNew, /delivery/, /contacts/ (EdostMap).
   */
  getOffices(
    payload: EdostOfficesPayload,
    isReset = false,
  ): Promise<EdostOfficesResponse | null> {
    return clientApiCall<EdostOfficesResponse>(
      `${config.API_SERVER_URL}/client/edost/getOffices/`,
      {
        api: "edost.getOffices",
        // Нужен для выбора доставки при оформлении заказа — сообщаем о любом сбое
        critical: true,
        params: {
          city: payload.city,
          region: payload.region,
          zip: payload.zip ?? "",
          reset: isReset,
        },
      },
    );
  },
  /**
   * Legacy Bitrix bxApi — напрямую, без BFF (старая корзина /personal/cart/).
   * Где вызывается: redux/action-creaters/cart.js → getEdostData().
   * удалится после перехода !!!
   */
  async getData(
    cityName: string,
    regionName = "",
    kladrId = "",
  ): Promise<EdostDataResponse | null> {
    const { data } = await axios.post(
      `${config.API_URL}/cart/getEdostData?city_name_new=${cityName}&region_name=${regionName}&kladr_id=${kladrId}`,
      {},
      { withCredentials: true },
    );
    return data;
  },
});
